import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  BellRing,
  BookOpenCheck,
  BrainCircuit,
  GalleryHorizontalEnd,
  Search,
  ShieldCheck,
  Store,
} from 'lucide-react';
import { appMeta, productFeatures, type FeatureId } from '@/config/iphonefastflip';

const icons: Record<FeatureId, React.ElementType> = {
  marketplaces: Store,
  profitability: BarChart3,
  priceBooks: BookOpenCheck,
  personalization: BrainCircuit,
  search: Search,
  favorites: BellRing,
  details: GalleryHorizontalEnd,
  account: ShieldCheck,
};

const Features: React.FC = () => (
  <main className="px-4 pb-24 pt-28 sm:pt-32">
    <section className="mx-auto max-w-7xl">
      <div className="mx-auto mb-16 max-w-3xl text-center">
        <p className="section-eyebrow">Funkcje iPhoneFastFlip</p>
        <h1 className="text-4xl font-black tracking-[-0.04em] text-white sm:text-6xl">
          Wszystkie narzędzia potrzebne do oceny ofert.
        </h1>
        <p className="section-copy mt-6">
          Funkcje działają na wspólnym koncie w Androidzie, Windowsie i przeglądarce. Web zmienia cały układ aplikacji wraz z szerokością okna.
        </p>
      </div>

      <div className="space-y-5">
        {productFeatures.map((feature, index) => {
          const Icon = icons[feature.id];
          return (
            <motion.article
              key={feature.id}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-70px' }}
              className="grid gap-7 rounded-[28px] border border-white/[0.08] bg-white/[0.025] p-6 sm:p-8 lg:grid-cols-[210px_1fr_1fr] lg:items-center"
            >
              <div>
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-blue-500/10 text-blue-300">
                  <Icon className="h-7 w-7" />
                </div>
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.22em] text-slate-600">Moduł {String(index + 1).padStart(2, '0')}</p>
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{feature.title}</h2>
                <p className="mt-3 text-sm leading-7 text-slate-400 sm:text-base">{feature.description}</p>
              </div>
              <ul className="space-y-3">
                {feature.bullets.map((bullet) => (
                  <li key={bullet} className="flex gap-3 text-sm leading-6 text-slate-300">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </motion.article>
          );
        })}
      </div>

      <section className="mt-16 overflow-hidden rounded-[32px] border border-blue-400/20 bg-gradient-to-br from-blue-500/15 via-white/[0.03] to-cyan-500/10 p-7 sm:p-10">
        <div className="flex flex-col items-start justify-between gap-7 lg:flex-row lg:items-center">
          <div>
            <p className="section-eyebrow">Sprawdź na swoim ekranie</p>
            <h2 className="max-w-2xl text-3xl font-black tracking-tight text-white sm:text-4xl">Ta sama aplikacja, bez instalowania osobnego demo.</h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-400">Uruchom wersję webową, zmień szerokość okna i zobacz dwa kompletne warianty interfejsu.</p>
          </div>
          <a href={appMeta.webUrl} className="button-primary shrink-0">
            Uruchom iPhoneFastFlip <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </section>
    </section>
  </main>
);

export default Features;
